import { School } from "@/common/types/school";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { Loading } from "@/components/atoms/Loading";
import { useQuery } from "@tanstack/react-query";
import { InfoList } from "@/components/organisms/InfoList";
import { useNavigate } from "react-router";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { useRecoilValue } from "recoil";
import { loginTypeState } from "@/common/states/user";
import { Guardian, Student } from "@/common/types/student";
import { useEffect, useState } from "react";
import { GET_SCHOOLS, GET_USER_INFO } from "@/common/querykeys";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";

const studentGender = {
  M: "남",
  F: "여",
};

export const UserInfoPage = () => {
  const navigate = useNavigate();
  const loginType = useRecoilValue(loginTypeState);
  const [userData, setUserData] = useState<any>([]);

  const { data: school } = useQuery<School>({
    queryKey: [GET_SCHOOLS],
    queryFn: async () => {
      return await request<School>({
        method: "GET",
        url: "/school",
      });
    },
  });

  const { data: student, isLoading } = useQuery<Student>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Student>({
        method: "GET",
        url: "/student/info",
      });
    },
    enabled: loginType === "student",
  });

  const { data: guardian } = useQuery<Guardian>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Guardian>({
        method: "GET",
        url: "/guardian/info",
      });
    },
    enabled: loginType === "guardian",
  });

  useEffect(() => {
    /** 학생 */
    if (loginType === "student") {
      if (student?.guardian) {
        /** 학생이고, 매칭된 보호자가 있습니다. */
        const userData = [
          {
            id: student?.userId,
            data_name: "계정 정보",
            data: [{ name: "아이디", data: student?.userId }],
          },
          {
            id: student?.id,
            data_name: "학생 정보",
            data: [
              { name: "이름", data: student?.name },
              { name: "생년월일", data: student?.birth },
              {
                name: "성별",
                data: student?.gender ? studentGender[student?.gender] : "",
              },
              {
                name: "연락처",
                data: student?.tel,
              },
              {
                name: "참여 유형",
                data: student?.studentCode.includes("P") ? "개인" : "단체",
              },
              { name: "학교명", data: student?.school?.name },
              { name: "학번", data: student?.studentIdNumber },
              { name: "UID", data: student?.id },
            ],
          },
          {
            id: student?.guardian?.id,
            data_name: "법정보호자 정보",
            data: [
              { name: "법정보호자 이름", data: student?.guardian?.name },
              { name: "법정보호자 생년월일", data: student?.guardian?.birth },
              { name: "법정보호자 연락처", data: student?.guardian?.tel },
            ],
          },
        ];
        setUserData(userData);
      } else {
        /** 학생이고, 매칭된 보호자가 없습니다. */
        const userData = [
          {
            id: student?.userId,
            data_name: "계정 정보",
            data: [{ name: "아이디", data: student?.userId }],
          },
          {
            id: student?.id,
            data_name: "학생 정보",
            data: [
              { name: "이름", data: student?.name },
              { name: "생년월일", data: student?.birth },
              {
                name: "성별",
                data: student?.gender ? studentGender[student?.gender] : "",
              },
              {
                name: "연락처",
                data: student?.tel,
              },
              {
                name: "참여 유형",
                data: student?.studentCode.includes("P") ? "개인" : "단체",
              },
              { name: "학교명", data: student?.school?.name },
              { name: "학번", data: student?.studentIdNumber },
              { name: "UID", data: student?.id },
            ],
          },
        ];
        setUserData(userData);
      }
      /** 법정보호자 */
    } else {
      if (guardian?.student) {
        /** 보호자이지만, 매칭된 학생이 있습니다. */
        if (Array.isArray(school)) {
          const foundSchool = school.find(
            (item) => item.id === guardian?.student?.studentCode.split("-")[0]
          );

          const userData = [
            {
              id: guardian.userId,
              data_name: "계정 정보",
              data: [
                { name: "아이디", data: guardian.userId },
                { name: "UID", data: guardian.id },
              ],
            },
            {
              id: student?.guardian?.id,
              data_name: "법정보호자 정보",
              data: [
                { name: "법정보호자 이름", data: guardian?.name },
                { name: "법정보호자 생년월일", data: guardian?.birth },
                { name: "법정보호자 연락처", data: guardian?.tel },
              ],
            },
            {
              id: student?.id,
              data_name: "학생 정보",
              data: [
                { name: "이름", data: guardian?.student?.name },
                { name: "생년월일", data: guardian?.student?.birth },
                {
                  name: "성별",
                  data: guardian?.student?.gender
                    ? studentGender[guardian?.student?.gender]
                    : "",
                },
                {
                  name: "연락처",
                  data: guardian?.student?.tel,
                },
                { name: "학교명", data: guardian?.student?.school.name },
                { name: "학번", data: guardian?.student?.studentIdNumber },
                { name: "UID", data: guardian?.student?.id },
              ],
            },
          ];
          setUserData(userData);
        }
      } else {
        /** 보호자이지만, 매칭된 학생이 없습니다. */
        const userData = [
          {
            id: guardian?.userId,
            data_name: "계정 정보",
            data: [
              { name: "아이디", data: guardian?.userId },
              { name: "UID", data: guardian?.id },
            ],
          },
          {
            id: student?.guardian?.id,
            data_name: "법정보호자 정보",
            data: [
              { name: "법정보호자 이름", data: guardian?.name },
              { name: "법정보호자 생년월일", data: guardian?.birth },
              { name: "법정보호자 연락처", data: guardian?.tel },
            ],
          },
        ];
        setUserData(userData);
      }
    }
  }, [loginType, student, school, guardian]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <MainTemplate
            nav={false}
            subPage={true}
            menuBtn={false}
            buttonLeft={
              <Buttons
                type="button"
                size="headerTextIcon"
                layout="text"
                color="bright100"
                icon={<IconArrow />}
                label="설정"
                onClick={() => navigate("/setting")}
              />
            }
            buttonRight={
              <Buttons
                type="button"
                size="headerTextIcon"
                layout="text"
                color="bright100"
                label="수정"
                onClick={() => navigate("/setting/user-editType")}
              />
            }
            pageTitle="내 정보"
          >
            <InfoList data={userData} />
          </MainTemplate>
        </>
      )}
    </>
  );
};
